import React, { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { documentAPI } from '../../../services/documentAPI';
import { platformAPI } from '../../../services/platformAPI';

export async function downloadEvidence(item) {
  const response = await documentAPI.downloadDocument(item.documentId);
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.download = item.name || 'document';
  link.click();
  window.URL.revokeObjectURL(url);
}

export function EvidenceLinks({ items = [] }) {
  const [error, setError] = useState('');
  const files = (items || []).filter((item) => item.documentId && item.name && !item.withheld);
  const withheld = (items || []).some((item) => item.withheld);
  if (!files.length && !withheld) return null;
  const open = async (item) => {
    try {
      await downloadEvidence(item);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Download was denied');
    }
  };
  return (
    <div>
      {files.map((item) => (
        <div key={String(item.documentId)} className="small">
          {item.name} · version {item.version || 1}
          <Button size="sm" variant="link" type="button" className="py-0" onClick={() => open(item)}>Download</Button>
        </div>
      ))}
      {withheld ? <div className="small text-muted">A document is attached. You do not have permission to open it.</div> : null}
      {error ? <div className="small text-danger">{error}</div> : null}
    </div>
  );
}

const EvidencePicker = ({
  tenderId,
  documents = [],
  onChange,
  label = 'Supporting documents',
  embedded = false,
  area = 'tender_documents',
  classification = 'standard'
}) => {
  const [types, setTypes] = useState([]);
  const [typeCode, setTypeCode] = useState('');
  const [library, setLibrary] = useState([]);
  const [pick, setPick] = useState('');
  const [error, setError] = useState('');

  const load = () => {
    documentAPI.getDocuments({ limit: 50 })
      .then((response) => setLibrary(response.data?.data?.documents || []))
      .catch(() => setLibrary([]));
  };

  useEffect(() => { load(); }, [tenderId]);

  useEffect(() => {
    platformAPI.listReference('document-types', { area }).then((response) => {
      const values = (response.data?.data?.values || []).filter((row) => row.status === 'active');
      setTypes(values);
      setTypeCode((current) => current || values[0]?.code || '');
    }).catch(() => setTypes([]));
  }, [area]);

  const add = (doc) => {
    if (!doc?._id) return;
    if (documents.some((item) => String(item.documentId) === String(doc._id))) return;
    onChange([...documents, {
      documentId: doc._id,
      name: doc.storage?.originalName || doc.name,
      version: doc.version || 1
    }]);
  };

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const body = new FormData();
    body.append('document', file);
    body.append('name', file.name);
    if (typeCode) body.append('typeCode', typeCode);
    body.append('documentArea', area);
    body.append('accessClassification', classification);
    if (tenderId) body.append('tenderId', tenderId);
    try {
      const response = await documentAPI.uploadDocument(body);
      const doc = response.data?.data?.document;
      if (doc?._id) {
        await platformAPI.classifyDocument(doc._id, {
          accessClassification: classification,
          tenderId: tenderId || undefined,
          aiContextEligible: false
        });
      }
      add(doc);
      load();
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'The file could not be uploaded');
    }
  };

  const download = async (item) => {
    try {
      await downloadEvidence(item);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Download was denied');
    }
  };

  const linked = documents.filter((item) => item.documentId && !item.withheld);
  const withheld = documents.filter((item) => item.withheld);

  return (
    <div className={embedded ? 'bg-evidence' : undefined}>
      {embedded ? null : <Form.Label className="small text-uppercase fw-bold text-muted">{label}</Form.Label>}
      {embedded ? null : <p className="small text-muted mb-2">Upload a file or choose one already stored. A typed name is not a document. These files are not made available to AI.</p>}
      {error ? <p className="small text-danger">{error}</p> : null}
      <ul className="list-unstyled mb-2">
        {linked.map((item) => (
          <li key={String(item.documentId)} className="d-flex flex-wrap gap-2 align-items-center">
            <span>{item.name} · version {item.version || 1}</span>
            <Button size="sm" variant="link" type="button" onClick={() => download(item)}>Download</Button>
            <Button size="sm" variant="link" type="button" onClick={() => onChange(documents.filter((row) => String(row.documentId) !== String(item.documentId)))}>Remove</Button>
          </li>
        ))}
        {withheld.map((item, index) => (
          <li key={`withheld-${index}`} className="small text-muted">A document is attached. You do not have permission to open it.</li>
        ))}
      </ul>
      <div className="row g-2">
        <div className="col-md-6">
          <Form.Select aria-label="Existing documents" value={pick} onChange={(event) => setPick(event.target.value)}>
            <option value="">Choose an existing document</option>
            {library.map((doc) => (
              <option key={doc._id} value={doc._id}>{doc.storage?.originalName || doc.name} · version {doc.version || 1}</option>
            ))}
          </Form.Select>
        </div>
        <div className="col-md-2">
          <Button size="sm" variant="outline-primary" type="button" disabled={!pick} onClick={() => {
            add(library.find((row) => String(row._id) === pick));
            setPick('');
          }}>Attach</Button>
        </div>
        <div className="col-md-4">
          <Form.Select aria-label="Document type" className="mb-2" value={typeCode} onChange={(event) => setTypeCode(event.target.value)}>
            {types.map((row) => <option key={row.code} value={row.code}>{row.label}</option>)}
          </Form.Select>
          <Form.Control type="file" aria-label="Upload supporting document" onChange={upload} />
        </div>
      </div>
    </div>
  );
};

export default EvidencePicker;
